import { Input } from 'antd';
import { Fragment, PureComponent, ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Tool, VitalsEntity } from '../../../../../store/types/prescription';
import { SERVER_VITALS, UnitsWithRangesEntity } from '../../../leftPanel/vitals';
import AntSelect from './AntSelect';
import { GROWTH_CHART_VITALS } from '../../../../../constants';
import { VitalsSelectedItemsType } from '../../../../../api/pushGenie';
import { upsertVitalsConfig } from '../../../../../store/actions/session';
import { getDaysFromWeeksAndDays, getWeeksAndDaysFromDays } from './growthChart/constants/utils';
import {
    BMI_ID,
    CMS_ID,
    FEET_ID,
    HEIGHT_ID,
    LBS_ID,
    WEIGHT_ID,
    vitalToIconMapping,
} from './VitalTable';
import moment from 'moment';
import { AntTooltip } from '../../../../common/AntTooltip';

type Props = {
    elements: ReadonlyArray<VitalsEntity>;
    containerId?: string;
    vitalConfigData?: VitalsSelectedItemsType[];
    isPreTerm?: boolean;
    visits?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string;
        flavour?: string;
        translateLang?: string;
    })[];
    onElementUpdate: (option: VitalsEntity, index?: number) => void;
    onElementDelete: (id: string, index?: number) => void;
    showGestationalError: string;
};

type State = {
    vitalConfigElement: VitalsEntity[];
    isLoadedFromIdb: boolean;
};

type VitalsEntityWithStatus = VitalsEntity & { status?: boolean };

class GrowthVitals extends PureComponent<Props, State> {
    UNITS_MAP: { [key: string]: UnitsWithRangesEntity[] | undefined | null } = {};
    state: State = {
        vitalConfigElement:
            this.props.vitalConfigData?.map((t) => ({
                id: t.id,
                name: t.name,
                dis_name: t.name,
                value: { qt: '', unit: '' },
            })) || [],
        isLoadedFromIdb: false,
    };

    setVitalsData = async (): Promise<void> => {
        const growthChartVitals = GROWTH_CHART_VITALS;
        const { vitalConfigData } = this.props;

        if (vitalConfigData === undefined) {
            return;
        }

        growthChartVitals.forEach((v) => {
            this.UNITS_MAP[v.id] = v.units_with_ranges;
        });

        const mdbVitalDbFormatData =
            growthChartVitals.map((v) => ({
                ...v,
                dis_name: v.dis_name,
                value: {
                    qt: '',
                    unit: (this.UNITS_MAP[v.id] as UnitsWithRangesEntity[])?.[0].dis_unit,
                },
            })) || [];

        const data =
            (vitalConfigData
                .map((cEle) => {
                    return mdbVitalDbFormatData.find((ele) => ele.id === cEle.id);
                })
                .filter((ele) => !!ele) as VitalsEntity[]) || [];

        this.setState({ vitalConfigElement: data, isLoadedFromIdb: true });
    };

    fillBMI = ({
        weight,
        height,
        weightUnit,
        heightUnit,
    }: {
        weight?: string;
        height?: string;
        weightUnit?: string;
        heightUnit?: string;
    }): void => {
        let weightValue;
        let heightValue;
        let BMI;

        const { elements, onElementUpdate } = this.props;

        const weightVital = elements.find((v) => v.id === WEIGHT_ID);

        const heightVital = elements.find((v) => v.id === HEIGHT_ID);

        const bmiVital = SERVER_VITALS.find((v) => v.id === BMI_ID);

        const weightVitalUnitId =
            weightUnit ||
            (
                this.UNITS_MAP[weightVital?.id || ''] &&
                (this.UNITS_MAP[weightVital?.id || ''] as UnitsWithRangesEntity[])
            )?.find((u) => u.dis_unit === weightVital?.value.unit || '')?.code_id ||
            '';

        if (weightVitalUnitId === LBS_ID) {
            weightValue = this.lbsToKgs(
                parseFloat(weight || weightVital?.value.qt || ''),
            ).toString();
        } else {
            weightValue = weight || weightVital?.value.qt;
        }

        const heightVitalUnitId =
            heightUnit ||
            (
                this.UNITS_MAP[heightVital?.id || ''] &&
                (this.UNITS_MAP[heightVital?.id || ''] as UnitsWithRangesEntity[])
            )?.find((u) => u.dis_unit === heightVital?.value.unit || '')?.code_id ||
            '';

        if (heightVitalUnitId === CMS_ID) {
            heightValue = this.cmsToMeter(
                parseFloat(height || heightVital?.value.qt || ''),
            ).toString();
        }
        if (heightVitalUnitId === FEET_ID) {
            const heightInFeet = height || heightVital?.value.qt;
            heightValue = this.feetToMeter(this.parseHeightInFeet(heightInFeet || '')).toString();
        }

        if (
            weightValue &&
            !isNaN(parseFloat(weightValue)) &&
            heightValue &&
            !isNaN(parseFloat(heightValue))
        ) {
            BMI = parseFloat(weightValue) / (parseFloat(heightValue) * parseFloat(heightValue));
            const isUpdate = elements.filter((v) => v.id === BMI_ID).length > 0 ? true : false;

            onElementUpdate(
                {
                    name: bmiVital?.name || '',
                    dis_name: bmiVital?.dis_name || '',
                    id: bmiVital?.id || '',
                    value: {
                        qt: BMI.toFixed(1) || '',
                        unit: (this.UNITS_MAP[bmiVital?.id || ''] as UnitsWithRangesEntity[])[0]
                            .dis_unit,
                    },
                    interpretation: undefined,
                    isoDate: new Date().toISOString(),
                    remark: undefined,
                },
                isUpdate ? elements.findIndex((v) => v.id === BMI_ID) : elements.length,
            );
        }
    };

    componentDidMount = (): void => {
        this.setVitalsData();
    };

    componentDidUpdate = (prevProps: Props): void => {
        if (
            JSON.stringify(prevProps.vitalConfigData) !== JSON.stringify(this.props.vitalConfigData)
        ) {
            this.setVitalsData();
        }
    };

    lbsToKgs = (weightInLbs: number): number => {
        return weightInLbs * 0.453592;
    };

    cmsToMeter = (cms: number): number => {
        return cms * 0.01;
    };

    feetToMeter = (feet: number): number => {
        return feet * 0.3048;
    };

    parseHeightInFeet = (height: string): number => {
        const heightInFeet = height.split("'");
        return (
            parseFloat(String(heightInFeet[0])) + (parseFloat(String(heightInFeet[1])) / 12 || 0)
        );
    };

    updateElement = (option: VitalsEntity, id: string): void => {
        const { elements, onElementUpdate, onElementDelete } = this.props;
        const index = elements.findIndex((el) => el.id === id);
        if (option.value.qt) {
            onElementUpdate(option, index === -1 ? undefined : index);
        } else {
            onElementDelete(option.id);
        }

        if (option.id === HEIGHT_ID || option.id === WEIGHT_ID) {
            setTimeout(() => {
                this.fillBMI({});
            }, 500);
        }
    };

    findElementUnits = (el: VitalsEntity): UnitsWithRangesEntityAndId[] | undefined => {
        const units =
            this.UNITS_MAP[el.id] && this.UNITS_MAP[el.id]?.map((v) => ({ ...v, id: v.code_id }));
        return (
            units ||
            (el.value.unit
                ? [
                      {
                          code: el.value.code || '',
                          code_id: el.value.code_id || '',
                          dis_unit: el.value.unit,
                          id: el.value.code_id || '',
                          safe: { low: '', high: '', type: 'string' },
                          system: '',
                      },
                  ]
                : undefined)
        );
    };

    findDefaultUnit = (el: VitalsEntity): UnitsWithRangesEntityAndId | undefined => {
        const units = this.findElementUnits(el);
        const currentVitalDefaultOptionId = this.props.vitalConfigData?.find((i) => i.id === el.id)
            ?.defaultOptionId;

        return (
            (el.value.qt
                ? units?.find((v) => v.dis_unit === el.value.unit)
                : units?.find((i) => i.code_id === currentVitalDefaultOptionId)) || units?.[0]
        );
    };

    render(): ReactNode {
        const { vitalConfigElement, isLoadedFromIdb } = this.state;
        const { elements, vitalConfigData, isPreTerm, showGestationalError } = this.props;

        const activeElements = vitalConfigElement
            .filter((v) => (isPreTerm ? true : v.id !== 'lb-5257276261'))
            .map((ele) => {
                const isElementPresent = elements.find((activeEle) => activeEle.id === ele.id);
                return isElementPresent ? { ...isElementPresent, status: true } : ele;
            });

        return (
            <div className="flex items-center items-center gap-x-16 gap-y-12 mt-16 pt-8 text-14 font-400">
                <div className="flex flex-wrap items-center">
                    {activeElements?.map((el, i) => {
                        return (
                            <VitalsCell
                                key={`${i}${el.locked}`}
                                el={el}
                                index={i}
                                updateElement={this.updateElement}
                                units={this.findElementUnits(el)}
                                defaultUnit={this.findDefaultUnit(el)}
                                isLoadedFromIdb={isLoadedFromIdb}
                                vitalConfig={vitalConfigData}
                                showGestationalError={
                                    el.id === 'lb-5257276261' ? showGestationalError : undefined
                                }
                            />
                        );
                    })}
                </div>
            </div>
        );
    }
}

interface UnitsWithRangesEntityAndId extends UnitsWithRangesEntity {
    id: string;
}

const VitalsCell: React.FC<{
    el: VitalsEntityWithStatus;
    index: number;
    updateElement: (option: VitalsEntity, id: string) => void;
    units: UnitsWithRangesEntityAndId[] | undefined | null;
    defaultUnit: UnitsWithRangesEntityAndId | undefined | null;
    isLoadedFromIdb: boolean;
    containerId?: string;
    vitalConfig?: VitalsSelectedItemsType[];
    showGestationalError?: string;
}> = ({
    el,
    updateElement,
    units,
    defaultUnit,
    isLoadedFromIdb,
    containerId,
    vitalConfig,
    showGestationalError,
}) => {
    const [selectedUnit, setSelectedUnit] = useState(defaultUnit);
    const dispatch = useDispatch();

    const onUnitChange = (option?: UnitsWithRangesEntityAndId) => {
        const currentVitalId = vitalConfig?.find((i) => i.id === el.id)?.id;
        const newVitalsConfig = vitalConfig?.map((ele) =>
            currentVitalId === ele.id ? { ...ele, defaultOptionId: option?.code_id } : ele,
        );
        dispatch(
            upsertVitalsConfig({
                selectedItems: newVitalsConfig || [],
            }),
        );
        setSelectedUnit(option);

        if (el.value.qt) {
            updateElement(
                {
                    ...el,
                    value: {
                        ...el.value,
                        unit: option?.dis_unit || '',
                        code: option?.code,
                        code_id: option?.code_id,
                        safe: option?.safe,
                    },
                },
                el.id,
            );
        }
    };

    useEffect(() => {
        setSelectedUnit(defaultUnit);
    }, [el.value.unit, el.value.qt]);

    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inp = e.target.value;
        if (selectedUnit?.code_id === 'vu-3019850218') {
            if (/^\d?\d?['.]?\d?\d?$/.test(inp)) {
                updateElement(
                    {
                        ...el,
                        value: {
                            ...el.value,
                            qt: e.target.value,
                            unit: selectedUnit?.dis_unit || el.value.unit,
                            code: selectedUnit?.code || el.value.code,
                            code_id: selectedUnit?.code_id || el.value.code_id,
                        },
                    },
                    el.id,
                );
            }
        } else {
            updateElement(
                {
                    ...el,
                    value: {
                        ...el.value,
                        qt: e.target.value,
                        unit: selectedUnit?.dis_unit || el.value.unit,
                        code: selectedUnit?.code || el.value.code,
                        code_id: selectedUnit?.code_id || el.value.code_id,
                    },
                },
                el.id,
            );
        }
    };

    const onChangeGestationalAge = (
        e: React.ChangeEvent<HTMLInputElement>,
        type: 'weeks' | 'days',
    ) => {
        if (type === 'weeks') {
            const remainingDays = getWeeksAndDaysFromDays(el.value.qt);
            const totalDays = getDaysFromWeeksAndDays(
                e.target.value,
                (remainingDays?.[1] || '')?.toString(),
            );

            if (totalDays <= 350) {
                updateElement(
                    {
                        ...el,
                        value: {
                            ...el.value,
                            qt: (totalDays || '')?.toString(),
                            unit: selectedUnit?.dis_unit || el.value.unit,
                            code: selectedUnit?.code || el.value.code,
                            code_id: selectedUnit?.code_id || el.value.code_id,
                        },
                    },
                    el.id,
                );
            }
        }

        if (type === 'days') {
            const remainingDays = getWeeksAndDaysFromDays(el.value.qt);
            const totalDays = getDaysFromWeeksAndDays(
                (remainingDays?.[0] || '')?.toString(),
                e.target.value,
            );

            if (totalDays <= 350) {
                updateElement(
                    {
                        ...el,
                        value: {
                            ...el.value,
                            qt: (totalDays || '')?.toString(),
                            unit: selectedUnit?.dis_unit || el.value.unit,
                            code: selectedUnit?.code || el.value.code,
                            code_id: selectedUnit?.code_id || el.value.code_id,
                        },
                    },
                    el.id,
                );
            }
        }
    };

    return (
        <div className="flex flex-col items-end w-full">
            <div
                className={`flex vital-cell rounded-8 flex-wrap lg:space-y-0 space-y-8 items-center justify-between w-full`}
            >
                <div
                    className={`flex flex-1 frequently-item-mh items-center  ${
                        el.status ? 'active ' : ''
                    } `}
                >
                    <div className="flex items-center group vital-section">
                        <div className="flex items-center justify-center min-w-32 min-h-32 w-32 h-32 text-12 rounded-full bg-darwin-neutral-50 toggler">
                            {vitalToIconMapping(el?.id)}
                        </div>
                        <p className="ml-8 word-break-word text-14 min-w-100">
                            {el?.dis_name || el?.name}
                        </p>
                    </div>
                </div>

                {el.id === 'lb-5257276261' ? (
                    <div className={`flex justify-between items-center`}>
                        <div
                            className={`vital-container flex items-center ml-16 space-x-8 overflow-hidden w-224 ${
                                isLoadedFromIdb
                                    ? el.value.qt
                                        ? 'filled-vitals-cell-input-container border-darwin-neutral-1000 rounded-2'
                                        : 'vitals-cell-input-container'
                                    : ''
                            }`}
                            style={{ fontWeight: 100, minHeight: 48 }}
                        >
                            <Input
                                type={selectedUnit?.safe?.type === 'Numeric' ? 'number' : 'text'}
                                allowClear
                                min={0}
                                onChange={(e) => onChangeGestationalAge(e, 'weeks')}
                                onPressEnter={(e) => {
                                    e.currentTarget.blur();
                                }}
                                value={getWeeksAndDaysFromDays(el.value.qt)?.[0] || ''}
                                className={`${
                                    el.value.qt ? 'border border-darwin-neutral-1000' : ''
                                }`}
                                addonAfter={
                                    <span className="px-4 text-10 font-700 text-darwin-neutral-400">
                                        wks
                                    </span>
                                }
                            />

                            <Input
                                type={selectedUnit?.safe?.type === 'Numeric' ? 'number' : 'text'}
                                allowClear
                                min={0}
                                onChange={(e) => onChangeGestationalAge(e, 'days')}
                                onPressEnter={(e) => {
                                    e.currentTarget.blur();
                                }}
                                value={getWeeksAndDaysFromDays(el.value.qt)?.[1] || ''}
                                className={`${
                                    el.value.qt ? 'border border-darwin-neutral-1000' : ''
                                }`}
                                addonAfter={
                                    <span className="px-4 text-10 font-700 text-darwin-neutral-400">
                                        days
                                    </span>
                                }
                            />
                            <AntTooltip
                                placement="left"
                                content="The gestational age has been calculated based on the previous visit gestational age"
                            >
                                <svg
                                    className="w-36 h-36 fill-neutral-3"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208S370.7 464 256 464zM296 336h-16V248C280 234.8 269.3 224 256 224H224C210.8 224 200 234.8 200 248S210.8 272 224 272h8v64h-16C202.8 336 192 346.8 192 360S202.8 384 216 384h80c13.25 0 24-10.75 24-24S309.3 336 296 336zM256 192c17.67 0 32-14.33 32-32c0-17.67-14.33-32-32-32S224 142.3 224 160C224 177.7 238.3 192 256 192z"></path>
                                </svg>
                            </AntTooltip>
                        </div>
                    </div>
                ) : (
                    <div className={`flex justify-between items-center`}>
                        <div
                            className={`vital-container flex items-center ml-16 space-x-8 overflow-hidden w-140 ${
                                isLoadedFromIdb
                                    ? el.value.qt
                                        ? 'filled-vitals-cell-input-container border-darwin-neutral-1000 rounded-2'
                                        : 'vitals-cell-input-container'
                                    : ''
                            }`}
                            style={{ fontWeight: 100, minHeight: 48 }}
                        >
                            <Input
                                type={selectedUnit?.safe?.type === 'Numeric' ? 'number' : 'text'}
                                min={0}
                                allowClear
                                onChange={onChangeHandler}
                                onPressEnter={(e) => {
                                    e.currentTarget.blur();
                                }}
                                value={el.value.qt}
                                className={`${
                                    el.value.qt ? 'border border-darwin-neutral-1000' : ''
                                }`}
                                addonAfter={
                                    isLoadedFromIdb && (
                                        <Fragment>
                                            {units && units.length > 0 && (
                                                <AntSelect
                                                    tabIndex={-1}
                                                    containerIdToAttachDropdown={containerId}
                                                    className="bg-transparent"
                                                    showArrow={false}
                                                    allowClear={false}
                                                    placeholder={'unit'}
                                                    optionFilterProp="children"
                                                    options={units}
                                                    customDropdownWidth={80}
                                                    setValue={onUnitChange}
                                                    filterOption={true}
                                                    value={selectedUnit || undefined}
                                                    getValue={(o) => o.dis_unit}
                                                    getLabelToCompare={(o) => o.dis_unit}
                                                    style={{ opacity: 0.6, fontSize: 10 }}
                                                />
                                            )}
                                        </Fragment>
                                    )
                                }
                            />
                        </div>
                    </div>
                )}
            </div>
            {showGestationalError && (
                <div className="mt-4">
                    <div className="px-12 py-4 flex items-center space-x-4 bg-status-errorBG rounded-8">
                        <div>
                            <svg
                                className="w-14 h-14 fill-status-error"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM232 152C232 138.8 242.8 128 256 128s24 10.75 24 24v128c0 13.25-10.75 24-24 24S232 293.3 232 280V152zM256 400c-17.36 0-31.44-14.08-31.44-31.44c0-17.36 14.07-31.44 31.44-31.44s31.44 14.08 31.44 31.44C287.4 385.9 273.4 400 256 400z"></path>
                            </svg>
                        </div>
                        <p className="text-13 text-status-error">{showGestationalError}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GrowthVitals;
